
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Volume2, UserRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { AIAgent } from '@/hooks/useCareerChat';

export interface ChatMessageProps {
  content: string;
  isUser: boolean;
  timestamp?: Date;
  onSpeakClick?: (text: string) => void;
  agent?: AIAgent | null;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ content, isUser, timestamp, onSpeakClick, agent }) => {
  const formattedTime = timestamp ? new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
  }).format(timestamp) : '';
  
  const handleSpeakClick = () => {
    if (onSpeakClick) {
      onSpeakClick(content);
    }
  };
  
  return (
    <div className={cn(
      "flex items-start gap-3 mb-4 max-w-3xl",
      isUser ? "ml-auto flex-row-reverse" : "mr-auto"
    )}>
      {/* Only show avatar for AI messages */}
      {!isUser && (
        <Avatar className="h-8 w-8 mt-1">
          <AvatarImage src={agent?.photo_url || "/placeholder.svg"} alt={agent?.name || "AI Assistant"} />
          <AvatarFallback className="bg-primary text-primary-foreground">
            {agent?.name?.charAt(0) || "AI"}
          </AvatarFallback>
        </Avatar>
      )}
      
      <div className={cn(
        "rounded-xl p-4 relative group",
        isUser 
          ? "bg-primary text-primary-foreground rounded-tr-none" 
          : "bg-muted rounded-tl-none"
      )}>
        <div className="whitespace-pre-wrap">{content}</div>
        
        <div className="flex items-center justify-between mt-1">
          {timestamp && (
            <div className={cn(
              "text-xs",
              isUser ? "text-primary-foreground/70" : "text-muted-foreground"
            )}>
              {formattedTime}
            </div>
          )}
          
          {/* Text-to-speech button for assistant messages */}
          {!isUser && onSpeakClick && (
            <Button 
              size="icon" 
              variant="ghost" 
              className={cn(
                "h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity",
                "absolute top-1 right-1 bg-background/80"
              )}
              onClick={handleSpeakClick}
              title="Listen to this message"
            >
              <Volume2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
